import { identity, unwrap } from "../api";

/**
 * Admin-side invitation management (list/create/resend/revoke), through
 * `InvitationController.cs`'s `api/identity/invitations` routes — distinct from `lib/identity.ts`'s
 * `previewInvitation`/`acceptInvitation`, which are the anonymous invitee-side half of the same
 * controller. Every route here requires `company.user.invite` (the controller's own `[Authorize]`);
 * see `pages/invitations/routes.tsx`'s header for what that means for this screen today.
 *
 * A runtime guard, not a hand-written contract (backend-gaps GAP-4/GAP-5 pattern): the published
 * document declares "200 OK" with no response schema on every one of these operations, so the
 * generated client types every response as `undefined`. `parseInvitationList` asserts the shape
 * instead of assuming it, same discipline as `lib/identity.ts`'s `parseAccount`/`parsePermissions`.
 */

export type InvitationSummary = {
  id: string;
  email: string | null;
  role: string;
  expiresAt: string;
  createdAt: string;
  createdBy: string | null;
  createdByName: string | null;
  companyName: string | null;
};

export type InvitationList = { items: InvitationSummary[]; totalCount: number };

function parseInvitation(raw: unknown): InvitationSummary | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  const id = typeof r.id === "string" ? r.id : null;
  const role = typeof r.role === "string" ? r.role : null;
  const expiresAt = typeof r.expiresAt === "string" ? r.expiresAt : null;
  const createdAt = typeof r.createdAt === "string" ? r.createdAt : null;
  if (id === null || role === null || expiresAt === null || createdAt === null) return null;

  return {
    id,
    role,
    expiresAt,
    createdAt,
    email: typeof r.email === "string" ? r.email : null,
    createdBy: typeof r.createdBy === "string" ? r.createdBy : null,
    createdByName: typeof r.createdByName === "string" ? r.createdByName : null,
    companyName: typeof r.companyName === "string" ? r.companyName : null,
  };
}

export function parseInvitationList(body: unknown): InvitationList {
  const raw = (body ?? {}) as Record<string, unknown>;
  const list = Array.isArray(raw.items) ? raw.items : null;
  const totalCount = typeof raw.totalCount === "number" ? raw.totalCount : null;

  if (list === null || totalCount === null) {
    throw new Error(
      "GET /api/identity/invitations answered without an items array and a totalCount. " +
        "The list screen has nothing to render without them.",
    );
  }

  const items = list.map(parseInvitation).filter((item): item is InvitationSummary => item !== null);
  return { items, totalCount };
}

/** Outstanding invitations in the caller's scope (FR-031) — not paged server-side, the whole set. */
export async function listInvitations(): Promise<InvitationList> {
  return parseInvitationList(await unwrap((await identity()).GET("/api/identity/invitations")));
}

/**
 * `companyRef` is deliberately not accepted here: it is honoured only for a caller holding
 * `identity.user.invite.any_company` (GAP-6), a code narrower than this screen's own gate and not
 * one this app has a reason to assume yet. Omitting it is what the backend already treats as the
 * common case — it falls back to the inviter's own company.
 */
export async function createInvitation(email: string, role: string): Promise<void> {
  await unwrap((await identity()).POST("/api/identity/invitations", { body: { email, role } }));
}

/** Rewrites the token and resends; the previous link stops working (FR-031). An expired one is resendable; 409 once accepted or revoked. */
export async function resendInvitation(id: string): Promise<void> {
  await unwrap((await identity()).POST("/api/identity/invitations/{id}/resend", { params: { path: { id } } }));
}

/** No reason field: `RevokeInvitationHandler` takes none. No replacement (FR-031). */
export async function revokeInvitation(id: string): Promise<void> {
  await unwrap((await identity()).DELETE("/api/identity/invitations/{id}", { params: { path: { id } } }));
}
